import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Paper, Typography, Select, MenuItem, FormControl,
  Tooltip, Chip, Skeleton,
} from '@mui/material';
import {
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  FolderSpecial as PathIcon,
  CheckCircle as PublishedIcon,
} from '@mui/icons-material';
import { useAdminNav } from '../../context/AdminNavContext';

/**
 * StoryPathNav — a compact "you are here, jump anywhere" bar for the admin
 * story pages. Lets you move to the previous / next story in the path or jump
 * straight to any sibling via a dropdown, without going back to the path page.
 *
 * Reads everything from AdminNavContext, so it's a drop-in with no props needed.
 *
 * @param {(storyId: string) => string} [buildTo] where a sibling story links to.
 *   Defaults to the translation/production center.
 */
const StoryPathNav = ({ buildTo }) => {
  const navigate = useNavigate();
  const { path, stories, storyId, currentIndex, prevStory, nextStory, loading } =
    useAdminNav();

  const linkFor =
    buildTo || ((id) => `/admin/stories/${id}/translations`);

  if (loading && stories.length === 0) {
    return (
      <Paper
        variant="outlined"
        sx={{ p: 1.5, mb: 2, borderRadius: 2, display: 'flex', gap: 1 }}
      >
        <Skeleton variant="rounded" width={110} height={32} />
        <Skeleton variant="rounded" width="100%" height={32} sx={{ flexGrow: 1 }} />
        <Skeleton variant="rounded" width={90} height={32} />
      </Paper>
    );
  }

  // Nothing useful to show if the story isn't part of a multi-story path.
  if (!path || stories.length <= 1 || currentIndex < 0) {
    return null;
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1, sm: 1.5 },
        mb: 2,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1, sm: 1.5 },
        flexWrap: { xs: 'wrap', md: 'nowrap' },
        bgcolor: 'background.paper',
      }}
    >
      <Tooltip title="Back to path">
        <Button
          size="small"
          startIcon={<PathIcon />}
          onClick={() => navigate(`/admin/paths/${path.path_id}`)}
          sx={{ textTransform: 'none', borderRadius: 2, flexShrink: 0, maxWidth: 220 }}
        >
          <Box
            component="span"
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {path.title}
          </Box>
        </Button>
      </Tooltip>

      <Button
        size="small"
        variant="outlined"
        startIcon={<PrevIcon />}
        disabled={!prevStory}
        onClick={() => prevStory && navigate(linkFor(prevStory.story_id))}
        sx={{ textTransform: 'none', borderRadius: 2, flexShrink: 0 }}
      >
        Prev
      </Button>

      {/* Jump dropdown — every story in the path, with position + status */}
      <FormControl size="small" sx={{ flexGrow: 1, minWidth: 180 }}>
        <Select
          value={storyId}
          onChange={(e) => navigate(linkFor(e.target.value))}
          renderValue={() => (
            <Typography variant="body2" noWrap>
              Story {currentIndex + 1} of {stories.length}
              {stories[currentIndex]?.title ? ` · ${stories[currentIndex].title}` : ''}
            </Typography>
          )}
        >
          {stories.map((s, i) => (
            <MenuItem key={s.story_id} value={s.story_id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Chip label={i + 1} size="small" sx={{ height: 20, minWidth: 26 }} />
                <Box component="span" sx={{ flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {s.title}
                </Box>
                {s.is_published && (
                  <PublishedIcon fontSize="small" color="success" />
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button
        size="small"
        variant="contained"
        endIcon={<NextIcon />}
        disabled={!nextStory}
        onClick={() => nextStory && navigate(linkFor(nextStory.story_id))}
        sx={{ textTransform: 'none', borderRadius: 2, flexShrink: 0 }}
      >
        Next
      </Button>
    </Paper>
  );
};

export default StoryPathNav;
